// @flow

// useTransitionManager turns a boolean into a quad-state value, basically
// adding intermediate states between true and false for a given period of time.
// This is useful for things like fading content in/out based on static values
// like isHovering.

import { useState, useRef, useEffect } from 'react';

export type TransitionState = 'inactive' | 'starting' | 'active' | 'ending';

export const DEFAULT_TRANSITION_DURATION_MS = 120;

// setAnimation is a helper that sets/clears timeouts and saves values to the
// given ref object. If transitionDuration is 0, it'll execute the given action
// synchronously.
function setAnimation(
  timeoutRef: {| current: ?TimeoutID |},
  transitionDuration: ?number,
  action?: () => void,
) {
  if (timeoutRef.current) {
    clearTimeout(timeoutRef.current);
  }

  timeoutRef.current = null;

  if (action) {
    if (transitionDuration) {
      timeoutRef.current = setTimeout(action, transitionDuration);
    } else {
      action();
    }
  }
}

// TODO (rjames): tests for this function
export default function useTransitionManager(
  immediateState: boolean,
  transitionInDuration: number = 0,
  transitionOutDuration: number = DEFAULT_TRANSITION_DURATION_MS,
): TransitionState {
  const timeoutRef = useRef<?TimeoutID>();

  const prevImmediateState = useRef(immediateState);

  const [state, setState] = useState<TransitionState>(immediateState ? 'active' : 'inactive');

  if (immediateState !== prevImmediateState.current) {
    if (immediateState) {
      if (state === 'inactive') {
        setState('starting');
        setAnimation(timeoutRef, transitionInDuration, () => {
          setState('active');
        });
      } else if (state === 'ending') {
        setState('active');
        setAnimation(timeoutRef);
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (state === 'active') {
        setState('ending');
        setAnimation(timeoutRef, transitionOutDuration, () => {
          setState('inactive');
        });
      } else if (state === 'starting') {
        setState('inactive');
        setAnimation(timeoutRef);
      }
    }
  }

  useEffect(() => () => setAnimation(timeoutRef), []);

  prevImmediateState.current = immediateState;

  return state;
}
